import React, { useCallback, useEffect, useState } from 'react';
import {
	EntityId,
	EntityType,
	VariableInstanceState,
} from '@elyx-code/project-logic-tree';
import { useParams } from 'react-router-dom';
import { IDialogProps } from '../../../lib/dialog/Dialog';
import { EditorService } from '../../../services/editor';
import VariableInstance from './VariableInstance';
import DialogLoader from '../../DialogLoader';
import { popupNotification } from '../../../popup-notification';
import EntityDialogHeader from '../../EntityDialogHeader';

export interface IVariableInstanceDialogContentProps extends IDialogProps {
	project: EditorService;
	ready: boolean;
}

export interface IVariableInstanceSectionProps {
	entity: VariableInstanceState;
	project: EditorService;
}

const VariableInstanceDialogContent: React.FC<
	IVariableInstanceDialogContentProps
> = ({ onClose, project, ready }) => {
	const { entityId } = useParams<{
		entityType: EntityType;
		entityId: EntityId;
	}>();

	const entity: VariableInstanceState | null =
		(project?.logic?.get(entityId) as VariableInstanceState | null) || null;

	const onEscapePressed = useCallback(
		(e: KeyboardEvent) => {
			// Or close the dialog if there is only one tab
			onClose(e);
		},
		[onClose]
	);

	const useForceUpdate = () => {
		const [_, setValue] = useState(0);
		return () => setValue((value) => value + 1);
	};
	const forceUpdate = useForceUpdate(); // Update the component

	useEffect(() => {
		project?.on('escape-key-press', onEscapePressed);

		if (!!entity) {
			project?.on(entity.id, () => {
				forceUpdate();
			});
		}

		return () => {
			project?.off('escape-key-press', onEscapePressed);
		};
	}, [project, entity, onEscapePressed]);

	if (!project || !entity) {
		if (!!project && project.hasLoadedLogic && !entity) {
			popupNotification({
				color: 'error',
				text: 'This variable instance does not exist in the current version of the project',
			});
			onClose(null);
			return;
		}

		return (
			<DialogLoader
				title='Variable instance node'
				subtitle='Variable instance builder'
				onClose={onClose}
			/>
		);
	}

	return (
		<div
			style={{
				display: 'flex',
				flexDirection: 'column',
				height: '100%',
				overflow: 'hidden',
				paddingBottom: 20,
			}}
		>
			<EntityDialogHeader entity={entity} project={project} onClose={onClose} />

			<div
				style={{
					display: 'flex',
					flexGrow: 1,
					overflowY: 'scroll',
				}}
			>
				{/* We need the 'span' for the react ref */}
				<span
					style={{
						maxHeight: '100%',
						maxWidth: '100%',
						flexGrow: 1,
						height: '100%',
					}}
				>
					<VariableInstance
						entity={entity}
						project={project}
						onClose={(e) => {
							onClose(e);
						}}
					/>
				</span>
			</div>
		</div>
	);
};

export default VariableInstanceDialogContent;
